<template>
  <div v-if="contactos.length" class="contactos-previos">
    <div class="cp-header">
      <span class="cp-titulo">
        Correos usados antes con este grupo en esta área
        <span class="cp-contador">{{ contactos.length }}</span>
      </span>
      <div class="cp-acciones">
        <button type="button" class="cp-link" @click="seleccionarTodos">Todos</button>
        <button type="button" class="cp-link" @click="limpiar">Ninguno</button>
      </div>
    </div>

    <ul class="cp-lista">
      <li v-for="c in contactos" :key="c.id" class="cp-item">
        <label>
          <input type="checkbox" :value="c.email" v-model="seleccionados" />
          <span class="cp-email">{{ c.email }}</span>
          <span v-if="c.nombre" class="cp-nombre">{{ c.nombre }}</span>
          <!-- Cuánto le queda de afiliación: lo que está por caducar se avisa
               antes, no el día que desaparece de la lista sin explicación. -->
          <span :class="['cp-vigencia', { 'por-vencer': porVencer(c) }]">
            {{ textoVigencia(c) }}
          </span>
        </label>
      </li>
    </ul>

    <button
      type="button"
      class="btn btn-sm guardar"
      :disabled="!seleccionados.length"
      @click="agregar"
    >
      Agregar {{ seleccionados.length || '' }} a la lista
    </button>
  </div>

  <p v-else-if="cargado" class="cp-vacio">
    Todavía no hay correos registrados para este grupo de interés en esta área.
    Los que envíes ahora quedarán guardados para el próximo formulario.
  </p>

  <!-- Sin área no hay directorio que consultar: se dice, en vez de no mostrar
       nada y dejar pensando que el banco no existe. -->
  <p v-else-if="idGrupoInteres && !idPrograma && !idEjecucion" class="cp-vacio">
    El banco de correos se guarda por grupo de interés y área. Esta ficha no
    tiene un área resuelta: ábrela desde su ejecución para ver los correos ya
    usados.
  </p>
</template>

<script lang="ts" setup>
/**
 * Selector de destinatarios ya usados, desde el banco de correos.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, watch } from 'vue'
import type { ContactoGrupo } from '@/services/CalificacionOpinionPreguntaService'
import { getContactosGrupoService } from '@/services/CalificacionOpinionPreguntaService'

const props = defineProps<{
  idGrupoInteres: number | null
  idPrograma?: number | null
  idEjecucion?: number | null
}>()

const emit = defineEmits<{
  (e: 'agregar', destinatarios: Array<{ email: string; nombre?: string | null }>): void
}>()

const contactos = ref<ContactoGrupo[]>([])
const seleccionados = ref<string[]>([])
const cargado = ref(false)

watch(
  () => [props.idGrupoInteres, props.idPrograma, props.idEjecucion] as const,
  async () => {
    contactos.value = []
    seleccionados.value = []
    cargado.value = false

    if (!props.idGrupoInteres) return
    // Sin área (ni ejecución de la que sacarla) no hay a qué directorio pedirle.
    if (!props.idPrograma && !props.idEjecucion) return

    contactos.value = await getContactosGrupoService(props.idGrupoInteres, {
      id_programa: props.idPrograma ?? null,
      id_ejecucion: props.idEjecucion ?? null
    })
    cargado.value = true
  },
  { immediate: true }
)

/** Días que le quedan de afiliación; null si no tiene caducidad declarada. */
function diasRestantes(c: ContactoGrupo): number | null {
  if (!c.vigencia_hasta) return null
  const hasta = new Date(c.vigencia_hasta)
  if (Number.isNaN(hasta.getTime())) return null
  const hoy = new Date()
  hoy.setHours(0, 0, 0, 0)
  return Math.ceil((hasta.getTime() - hoy.getTime()) / 86_400_000)
}

/** Se avisa a partir de 6 meses: da margen para renovar la afiliación. */
function porVencer(c: ContactoGrupo): boolean {
  const dias = diasRestantes(c)
  return dias !== null && dias <= 180
}

function textoVigencia(c: ContactoGrupo): string {
  const dias = diasRestantes(c)
  if (dias === null) return 'sin caducidad'
  if (dias <= 30) return `vence en ${dias} día${dias === 1 ? '' : 's'}`
  if (dias <= 180) return `vence en ${Math.round(dias / 30)} meses`
  return `vigente hasta ${c.vigencia_hasta}`
}

function seleccionarTodos() {
  seleccionados.value = contactos.value.map(c => c.email)
}

function limpiar() {
  seleccionados.value = []
}

function agregar() {
  const elegidos = contactos.value
    .filter(c => seleccionados.value.includes(c.email))
    .map(c => ({ email: c.email, nombre: c.nombre ?? null }))

  emit('agregar', elegidos)
  seleccionados.value = []
}
</script>

<style scoped>
.contactos-previos {
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  padding: 0.7rem 0.85rem;
  background: #fafcff;
  margin-bottom: 0.9rem;
}

.cp-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.cp-titulo {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f4e79;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.cp-contador {
  background: #0e69a0;
  color: #fff;
  border-radius: 10px;
  padding: 0 0.45rem;
  font-size: 0.7rem;
}

.cp-acciones {
  display: flex;
  gap: 0.6rem;
}

.cp-link {
  border: none;
  background: none;
  color: #0e69a0;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}

.cp-lista {
  list-style: none;
  margin: 0 0 0.6rem;
  padding: 0;
  max-height: 170px;
  overflow-y: auto;
}

.cp-item label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.2rem 0;
  font-size: 0.8rem;
  cursor: pointer;
}

.cp-email {
  font-weight: 500;
}

.cp-nombre {
  color: #888;
  font-size: 0.75rem;
}

.cp-vigencia {
  margin-left: auto;
  font-size: 0.7rem;
  color: #999;
  white-space: nowrap;
}

.cp-vigencia.por-vencer {
  color: #b06000;
  font-weight: 600;
}

.cp-vacio {
  margin: 0 0 0.9rem;
  font-size: 0.78rem;
  color: #888;
}

.btn {
  padding: 8px 18px;
  font-weight: 600;
  font-size: 0.8rem;
  font-family: inherit;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.btn-sm {
  padding: 0.35rem 0.75rem;
  font-size: 0.75rem;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.guardar {
  background: #eea222;
  color: #000;
}

.btn.guardar:not(:disabled):hover {
  background: #0e69a0;
  color: #fff;
}
</style>
